// src/pages/AllEpisodesPage.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const Container = styled(motion.div)`
  padding: 100px 5% 50px 5%;
  min-height: 100vh;
  background: var(--background-start);
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 10px;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--secondary-color);
  margin-bottom: 30px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 25px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
  }
`;

// Bölüm kartı için stiller (aynı)
const EpisodeBox = styled.div`
  width: ${props => props.$isMobile ? '150px' : '280px'};
  height: ${props => props.$isMobile ? '220px' : '160px'};
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-5px);
    border-color: var(--gradient-pink);
    box-shadow: 0 10px 30px rgba(255, 0, 127, 0.3);
  }
`;

const EpisodeImage = styled.div`
  width: 100%;
  height: ${props => props.$isMobile ? '120px' : '100%'};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  background-color: ${props => props.image ? 'transparent' : 'var(--glass-bg)'};

  ${props => !props.image && `
    &::after {
      content: '${props.alt || 'Resim Yok'}';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1a102a, #0b0614);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--secondary-color);
      font-size: 0.7rem;
      text-align: center;
      padding: 10px;
      box-sizing: border-box;
    }
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.$isMobile 
      ? 'linear-gradient(to bottom, transparent 40%, rgba(10, 2, 28, 0.95) 100%)' 
      : 'linear-gradient(to bottom, transparent 40%, rgba(10, 2, 28, 0.95) 100%)'};
    z-index: 1;
  }
`;

const TypeTag = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--primary-color);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.5rem;
  font-weight: 600;
  z-index: 4;
  border: 1px solid var(--border-color);
`;

const EpisodeDate = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 600;
  color: var(--primary-color);
  border: 1px solid var(--border-color);
  z-index: 4;
`;

const EpisodeInfo = styled.div`
  position: ${props => props.$isMobile ? 'relative' : 'absolute'};
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${props => props.$isMobile ? '10px 8px' : '8px'};
  color: white;
  z-index: 2;
  height: ${props => props.$isMobile ? 'auto' : 'auto'};
  background: ${props => props.$isMobile ? 'transparent' : 'transparent'};
`;

const EpisodeTitle = styled.h3`
  font-size: ${props => props.$isMobile ? '0.7rem' : '0.9rem'};
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EpisodeMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${props => props.$isMobile ? '0.6rem' : '0.75rem'};
  color: var(--secondary-color);
`;

const EpisodeNumber = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.55rem;
  font-weight: 600;
  color: var(--gradient-cyan);
`;

const SeasonNumber = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.55rem;
  font-weight: 600;
  color: var(--primary-color);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 10px 15px;
  background: ${props => props.active ? 'var(--gradient-pink)' : 'var(--glass-bg)'};
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: ${props => props.active ? 'white' : 'var(--primary-color)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--gradient-pink);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--secondary-color);
  font-size: 1rem;
`;

const EPISODES_PER_PAGE = 20;

// OPTİMİZE EDİLMİŞ: Tüm bölümleri tek API call ile çek
const getAllEpisodesFromMySQL = async () => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.GET_EPISODES));
    
    if (!response.ok) {
      throw new Error('Bölümler çekilemedi');
    }
    
    const data = await response.json();
    
    if (data.success && data.episodes) {
      // API'den gelen veriyi işle
      const processedEpisodes = data.episodes.map(episode => {
        const episodeDate = episode.air_date ? new Date(episode.air_date) : 
                          episode.created_at ? new Date(episode.created_at) : new Date();
        
        return {
          tmdb_id: episode.tmdb_id,
          title: episode.content_title,
          media_type: episode.media_type,
          backdrop_path: episode.backdrop_path,
          poster_path: episode.poster_path,
          vote_average: episode.vote_average,
          season: episode.season_number,
          episode: episode.episode_number,
          episodeData: episode,
          episodeDate: episodeDate,
          contentId: episode.content_id,
          episodeTitle: episode.episode_title,
          episodeOverview: episode.episode_overview,
          thumbnailPath: episode.thumbnail_path,
          airDate: episode.air_date
        };
      });
      
      return processedEpisodes;
    } else {
      throw new Error(data.error || 'Geçersiz veri formatı');
    }
    
  } catch (error) {
    console.error("Bölüm çekerken hata:", error);
    return [];
  }
};

// Bölüm verilerini formatlama (aynı)
const formatEpisodeData = (episodeItem) => {
  const typeMap = { 
    movie: 'Film', 
    tv: 'Dizi',
    anime: 'Anime'
  };

  const episodeDate = episodeItem.episodeDate;
  
  let formattedDate = 'Yeni';
  if (episodeDate && episodeDate instanceof Date && !isNaN(episodeDate)) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (episodeDate.getFullYear() < today.getFullYear()) {
      formattedDate = `${episodeDate.getDate()} ${episodeDate.toLocaleDateString('tr-TR', { month: 'long' })} ${episodeDate.getFullYear()}`;
    } 
    else if (episodeDate.toDateString() === today.toDateString()) {
      formattedDate = 'Bugün';
    } else if (episodeDate.toDateString() === yesterday.toDateString()) {
      formattedDate = 'Dün';
    } 
    else {
      formattedDate = `${episodeDate.getDate()} ${episodeDate.toLocaleDateString('tr-TR', { month: 'short' })}`;
    }
  }

  const getImageUrl = (path, size = 'w500') => {
    // Path kontrolü - boş, null, undefined veya sadece whitespace ise null döndür
    if (!path || path === 'null' || path === 'undefined' || (typeof path === 'string' && path.trim() === '')) {
      return null;
    }
    
    // String'e çevir ve trim yap
    path = String(path).trim();
    
    // Zaten tam URL ise direkt döndür
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // Zaten proxy URL'si ise direkt döndür
    if (path.includes('/proxy-image.php')) {
      return path;
    }
    
    // Zaten data URI ise direkt döndür
    if (path.startsWith('data:')) {
      return path;
    }
    
    // Eğer path tam bir TMDB URL'si ise, sadece path kısmını çıkar
    if (path.includes('image.tmdb.org')) {
      const match = path.match(/\/t\/p\/[^/]+\/(.+)$/);
      if (match) {
        path = '/' + match[1];
      }
    }
    
    // Path'i normalize et - başında / olmalı (http/https ile başlamıyorsa)
    let normalizedPath = path;
    if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
      normalizedPath = '/' + normalizedPath;
    }
    
    // Proxy URL oluştur
    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(normalizedPath)}&size=${size}`;
  };

  const imageUrl = episodeItem.thumbnailPath ? 
    getImageUrl(episodeItem.thumbnailPath) : 
    episodeItem.backdrop_path ? 
    getImageUrl(episodeItem.backdrop_path) : 
    episodeItem.poster_path ? 
    getImageUrl(episodeItem.poster_path) : 
    null;

  return {
      title: episodeItem.title,
      imageUrl: imageUrl,
      season: episodeItem.season,
      episode: episodeItem.episode,
      episodeTitle: episodeItem.episodeTitle || `${episodeItem.season}.Sezon ${episodeItem.episode}.Bölüm`,
      episodeDate: formattedDate,
      mediaTypeLabel: typeMap[episodeItem.media_type] || 'İçerik'
  };
};

const AllEpisodesPage = () => {
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchEpisodes = async () => {
      setLoading(true);
      try {
        // OPTİMİZE: Tek API call ile tüm bölümleri çek
        const episodes = await getAllEpisodesFromMySQL();
        setAllEpisodes(episodes);
        
      } catch (error) {
        console.error('Bölümler yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodes();
  }, []);

  // Sayfalama
  const totalPages = Math.ceil(allEpisodes.length / EPISODES_PER_PAGE);
  const startIndex = (currentPage - 1) * EPISODES_PER_PAGE;
  const currentEpisodes = allEpisodes.slice(startIndex, startIndex + EPISODES_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageButton
          key={i}
          active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    return pages;
  };

  if (loading) {
    return (
      <Container>
        <Header />
        <LoadingText>Bölümler yükleniyor...</LoadingText>
      </Container>
    );
  }

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header />
      
      <Title>Tüm Bölümler</Title>
      <Subtitle>
        {allEpisodes.length} bölüm - Sayfa {currentPage} / {totalPages}
      </Subtitle>

      <CardsGrid>
        {currentEpisodes.map((episodeItem, index) => {
          const formattedEpisode = formatEpisodeData(episodeItem);
          
          return (
            <EpisodeBox 
              key={`${episodeItem.contentId}-${episodeItem.season}-${episodeItem.episode}-${index}`}
              $isMobile={isMobile}
              onClick={() => {
                const watchType = episodeItem.media_type === 'anime' ? 'tv' : episodeItem.media_type;
                window.location.href = `/watch/${watchType}/${episodeItem.tmdb_id}/season/${episodeItem.season}/episode/${episodeItem.episode}`;
              }}
            >
              <EpisodeImage 
                image={formattedEpisode.imageUrl} 
                $isMobile={isMobile}
              />
              
              <TypeTag>{formattedEpisode.mediaTypeLabel}</TypeTag>
              
              <EpisodeDate>{formattedEpisode.episodeDate}</EpisodeDate>

              <EpisodeInfo $isMobile={isMobile}>
                <EpisodeTitle $isMobile={isMobile}>
                  {formattedEpisode.title}
                </EpisodeTitle>
                
                <EpisodeMeta $isMobile={isMobile}>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <SeasonNumber>
                      {episodeItem.season}. Sezon
                    </SeasonNumber>
                    <EpisodeNumber>
                      {episodeItem.episode}. Bölüm
                    </EpisodeNumber>
                  </div>
                </EpisodeMeta>
              </EpisodeInfo>
            </EpisodeBox>
          );
        })}
      </CardsGrid>

      {totalPages > 1 && (
        <Pagination>
          <PageButton
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Önceki
          </PageButton>
          
          {renderPageNumbers()}
          
          <PageButton
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Sonraki
          </PageButton>
        </Pagination>
      )}
    </Container>
  );
};

export default AllEpisodesPage;
